'use client'

import styled, { css } from 'styled-components'

import { spacing, styles } from '@fortum/elemental-ui'

import { PageCardWrapper } from '@/shared/components/PageCard/PageCardWrapper'
import type { ArticleEntry } from '@/shared/contentful/types'
import { getSizesForColumns } from '@/shared/utils/image'

export type ArticleGridProps = {
  articles: ArticleEntry[]
  /**
   * Show alternating two and three columns per row on larger screens.
   */
  showMixedColumns: boolean
}

export const ArticleGrid = ({ articles, showMixedColumns }: ArticleGridProps) => {
  return (
    <CardGrid $showMixedColumns={showMixedColumns}>
      {articles.map((article) => (
        <PageCardWrapper
          key={article.sys.id}
          page={article}
          showDescription={false}
          imageSizes={getSizesForColumns({ m: 6, l: 6 })}
        />
      ))}
    </CardGrid>
  )
}

const CardGrid = styled.div<{ $showMixedColumns: boolean }>`
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  gap: ${spacing.xs} 0.75rem;

  & > * {
    grid-column: span 12;
  }

  ${styles.breakpoint.s} {
    column-gap: ${spacing.xxs};
  }

  ${styles.breakpoint.m} {
    & > * {
      grid-column: span 6;
    }

    /* Use three columns per row every second row, except if that row would have empty columns */
    ${({ $showMixedColumns: $isMixedColumns }) =>
      $isMixedColumns &&
      css`
        /* stylelint-disable no-descending-specificity */
        & > *:nth-child(5n + 3):not(:nth-last-child(2), :nth-last-child(1)),
        & > *:nth-child(5n + 4):not(:nth-last-child(1)),
        & > *:nth-child(5n + 5) {
          grid-column: span 4;
        }
      `}
  }

  ${styles.breakpoint.l} {
    column-gap: ${spacing.xs};
  }
`
